/*
  Discount Materials — ultimate predictive search add-ons.

  Adds to the existing <predictive-search> Web Component:
    - In-row "Add to basket" button (single-variant products) via /cart/add.js,
      with a brief success state and a global cart-update publish so the cart
      drawer/bubble update without a page reload.
    - "Options" button for multi-variant products (links to PDP).
    - Recent searches: persisted in localStorage, rendered when the input is
      focused with an empty value.
    - Better keyboard hint: ⏎ submit / ↑↓ navigate / Esc close.
    - Slightly more aggressive trigger: kicks in from 1 character.
*/

(function () {
  const RECENT_KEY = 'dbm_recent_searches';
  const RECENT_MAX = 6;

  function readRecent() {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.slice(0, RECENT_MAX) : [];
    } catch (_e) {
      return [];
    }
  }

  function writeRecent(list) {
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (_e) {
      /* quota / private mode */
    }
  }

  function pushRecent(term) {
    const cleaned = (term || '').trim();
    if (cleaned.length < 2) return;
    const list = readRecent().filter((t) => t.toLowerCase() !== cleaned.toLowerCase());
    list.unshift(cleaned);
    writeRecent(list);
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /* ---------------------------------------------------------------- */
  /* Empty-state: recent / popular searches                           */
  /* ---------------------------------------------------------------- */
  function renderEmptyState(root) {
    const popular = (window.DBMSearchPopular || []).filter(Boolean);
    const recent = readRecent();
    if (!popular.length && !recent.length) return '';

    let html = '<div class="dbm-predictive__empty">';

    if (recent.length) {
      html += '<div class="dbm-predictive__empty-block">';
      html += '<h3 class="dbm-predictive__empty-title">';
      html += '<span>Recent searches</span>';
      html += '<button type="button" class="dbm-predictive__empty-clear" data-dbm-clear-recent>Clear</button>';
      html += '</h3><ul class="dbm-predictive__empty-list">';
      recent.forEach((term) => {
        const safe = escapeHtml(term);
        html +=
          `<li><a class="dbm-predictive__empty-link" href="/search?q=${encodeURIComponent(term)}">` +
          `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">` +
          `<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>${safe}</a></li>`;
      });
      html += '</ul></div>';
    }

    if (popular.length) {
      html += '<div class="dbm-predictive__empty-block">';
      html += '<h3 class="dbm-predictive__empty-title"><span>Popular right now</span></h3>';
      html += '<ul class="dbm-predictive__empty-chips">';
      popular.forEach((term) => {
        const safe = escapeHtml(term);
        html += `<li><a class="dbm-predictive__chip" href="/search?q=${encodeURIComponent(term)}">${safe}</a></li>`;
      });
      html += '</ul></div>';
    }

    html += '</div>';
    return html;
  }

  function showEmptyState(component) {
    // Prefer the inner [data-predictive-search-results] element when present,
    // but fall back to the [data-predictive-search] wrapper. The wrapper is
    // safe to write into because `.predictive-search-status` (required by
    // Shopify's predictive-search.js) lives OUTSIDE it as a sibling — keep it
    // that way in any search markup.
    const wrapper = component.querySelector('[data-predictive-search]');
    if (!wrapper) return;
    const target = wrapper.querySelector('[data-predictive-search-results]') || wrapper;
    const html = renderEmptyState(component);
    if (!html) return;
    target.innerHTML = html;
    component.setAttribute('open', 'true');
    component.setAttribute('results', 'true');
    const input = component.querySelector('input[type="search"]');
    if (input) input.setAttribute('aria-expanded', 'true');
  }

  /* ---------------------------------------------------------------- */
  /* Cart state — variant quantities power the in-row steppers and    */
  /* the "View basket" bar. Kept fresh by the cartUpdate pub-sub      */
  /* event (published by DBM.addToCart / DBM.updateCartQty and Dawn). */
  /* ---------------------------------------------------------------- */
  let cartQty = null; // { [variantId: string]: lineQuantity }
  let cartCount = 0;

  function applyCartState(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    cartQty = {};
    cart.items.forEach((item) => {
      cartQty[String(item.variant_id)] = item.quantity;
    });
    cartCount = cart.item_count || 0;
    syncAllRows();
  }

  let cartFetchPromise = null;
  function ensureCartState() {
    if (cartQty || cartFetchPromise) return cartFetchPromise || Promise.resolve();
    cartFetchPromise = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((res) => (res.ok ? res.json() : null))
      .then((cart) => {
        if (cart) applyCartState(cart);
        else cartQty = cartQty || {};
      })
      .catch(() => {
        cartQty = cartQty || {};
      });
    return cartFetchPromise;
  }

  /* ---------------------------------------------------------------- */
  /* In-row Add to Basket + quantity stepper.                          */
  /* First click adds via the shared DBM.addToCart helper (silent —   */
  /* no drawer popup, dropdown stays open). Once a product is in the  */
  /* basket its row shows a − qty + stepper instead of the Add        */
  /* button, so quantity is managed right from the search results.    */
  /* ---------------------------------------------------------------- */
  async function addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled || !window.DBM || typeof window.DBM.addToCart !== 'function') return;
    const original = button.innerHTML;
    button.disabled = true;
    button.classList.add('is-loading');
    button.innerHTML = '<span class="dbm-predictive__atc-spinner" aria-hidden="true"></span>';

    try {
      const cart = await window.DBM.addToCart({ variantId, quantity: 1, source: 'predictive-search' });
      button.classList.remove('is-loading');
      button.innerHTML = original;
      button.disabled = false;
      applyCartState(cart); // hides the button, shows the stepper
    } catch (err) {
      console.error('[dbm-search] add-to-cart failed', err);
      button.classList.remove('is-loading');
      button.classList.add('is-error');
      button.innerHTML = '<span>Try again</span>';
      setTimeout(() => {
        button.classList.remove('is-error');
        button.innerHTML = original;
        button.disabled = false;
      }, 1800);
    }
  }

  // At quantity 1 the minus button removes the line — show a bin so
  // the customer knows what will happen before they tap it.
  const TRASH_SVG =
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
    '<path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>' +
    '<path d="M10 11v6M14 11v6"/></svg>';

  function minusContent(qty) {
    return qty <= 1 ? TRASH_SVG : '&minus;';
  }

  function minusLabel(qty) {
    return qty <= 1 ? 'Remove from basket' : 'Decrease quantity';
  }

  function stepperHtml(variantId, qty) {
    const removeClass = qty <= 1 ? ' is-remove' : '';
    return (
      `<div class="dbm-predictive__qty" data-dbm-qty data-variant-id="${escapeHtml(variantId)}">` +
      `<button type="button" class="dbm-predictive__qty-btn dbm-predictive__qty-btn--minus${removeClass}" data-dbm-qty-delta="-1" aria-label="${minusLabel(qty)}">${minusContent(qty)}</button>` +
      `<span class="dbm-predictive__qty-num" aria-live="polite">${qty}</span>` +
      '<button type="button" class="dbm-predictive__qty-btn dbm-predictive__qty-btn--plus" data-dbm-qty-delta="1" aria-label="Increase quantity">+</button>' +
      '</div>'
    );
  }

  /* Show either the Add button or the stepper for one result row,
     depending on whether the variant is in the basket. Idempotent —
     it only touches the DOM when something actually changed (the
     re-render MutationObserver depends on that to settle). */
  function syncRow(action) {
    const btn = action.querySelector('[data-dbm-add-to-cart]');
    if (!btn) return;
    const variantId = String(btn.dataset.variantId || '');
    const qty = (cartQty && cartQty[variantId]) || 0;
    const stepper = action.querySelector('[data-dbm-qty]');

    if (qty > 0) {
      if (!btn.hidden) btn.hidden = true;
      if (!stepper) {
        btn.insertAdjacentHTML('afterend', stepperHtml(variantId, qty));
      } else {
        const num = stepper.querySelector('.dbm-predictive__qty-num');
        if (num && num.textContent !== String(qty)) num.textContent = String(qty);
        const minus = stepper.querySelector('.dbm-predictive__qty-btn--minus');
        if (minus && minus.classList.contains('is-remove') !== (qty <= 1)) {
          minus.classList.toggle('is-remove', qty <= 1);
          minus.innerHTML = minusContent(qty);
          minus.setAttribute('aria-label', minusLabel(qty));
        }
      }
    } else {
      if (stepper) stepper.remove();
      if (btn.hidden) btn.hidden = false;
    }
  }

  /* "View basket (n)" bar pinned to the bottom of an open dropdown. */
  function syncCartBar(component) {
    const wrapper = component.querySelector('[data-predictive-search]');
    if (!wrapper) return;
    let bar = wrapper.querySelector('[data-dbm-cartbar]');
    const hasContent = wrapper.children.length > (bar ? 1 : 0);

    if (!cartCount || !hasContent) {
      if (bar) bar.remove();
      return;
    }
    const label = `View basket (${cartCount})`;
    if (!bar) {
      wrapper.insertAdjacentHTML(
        'beforeend',
        '<div class="dbm-predictive__cartbar" data-dbm-cartbar>' +
          `<button type="button" class="dbm-predictive__cartbar-btn" data-dbm-view-cart><span>${label}</span>` +
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M5 12h14m-6-6 6 6-6 6"/></svg>' +
          '</button></div>'
      );
    } else {
      const span = bar.querySelector('[data-dbm-view-cart] span');
      if (span && span.textContent !== label) span.textContent = label;
    }
  }

  function syncAllRows() {
    document.querySelectorAll('predictive-search').forEach((component) => {
      component.querySelectorAll('.dbm-predictive__product-action').forEach(syncRow);
      syncCartBar(component);
    });
  }

  async function changeQty(btn) {
    const wrap = btn.closest('[data-dbm-qty]');
    if (!wrap || wrap.classList.contains('is-busy')) return;
    if (!window.DBM || typeof window.DBM.updateCartQty !== 'function') return;
    const variantId = wrap.dataset.variantId;
    const current = (cartQty && cartQty[variantId]) || 0;
    const delta = parseInt(btn.dataset.dbmQtyDelta, 10) || 0;
    const next = Math.max(0, current + delta);
    if (next === current) return;

    wrap.classList.add('is-busy');
    try {
      const cart = await window.DBM.updateCartQty({
        variantId,
        quantity: next,
        source: 'predictive-search',
      });
      applyCartState(cart); // stock caps come back in the payload
    } catch (err) {
      console.error('[dbm-search] quantity update failed', err);
    }
    wrap.classList.remove('is-busy');
    syncAllRows();
  }

  /* ---------------------------------------------------------------- */
  /* Wire up                                                           */
  /* ---------------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const addBtn = event.target.closest && event.target.closest('[data-dbm-add-to-cart]');
    if (addBtn) {
      event.preventDefault();
      event.stopPropagation();
      addToCart(addBtn);
      return;
    }

    const qtyBtn = event.target.closest && event.target.closest('[data-dbm-qty-delta]');
    if (qtyBtn) {
      event.preventDefault();
      event.stopPropagation();
      changeQty(qtyBtn);
      return;
    }

    const viewCart = event.target.closest && event.target.closest('[data-dbm-view-cart]');
    if (viewCart) {
      event.preventDefault();
      event.stopPropagation();
      const component = viewCart.closest('predictive-search');
      if (component) {
        if (typeof component.close === 'function') component.close(true);
        else {
          component.removeAttribute('open');
          component.removeAttribute('results');
        }
      }
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
      else window.location.href = '/cart';
      return;
    }

    const closeBtn = event.target.closest && event.target.closest('[data-dbm-predictive-close]');
    if (closeBtn) {
      event.preventDefault();
      event.stopPropagation();
      const component = closeBtn.closest('predictive-search');
      if (component) {
        if (typeof component.close === 'function') component.close(true);
        else {
          component.removeAttribute('open');
          component.removeAttribute('results');
        }
      }
      return;
    }

    const clearRecent = event.target.closest && event.target.closest('[data-dbm-clear-recent]');
    if (clearRecent) {
      event.preventDefault();
      event.stopPropagation();
      writeRecent([]);
      const component = clearRecent.closest('predictive-search');
      if (component) {
        const wrapper = component.querySelector('[data-predictive-search]');
        const target = wrapper && (wrapper.querySelector('[data-predictive-search-results]') || wrapper);
        if (target) target.innerHTML = '';
        showEmptyState(component);
        // Nothing left to show (no popular terms either) — close the dropdown.
        if (target && !target.innerHTML) {
          if (typeof component.close === 'function') component.close(true);
          else {
            component.removeAttribute('open');
            component.removeAttribute('results');
          }
        }
      }
      return;
    }
  });

  // Track submitted search terms as "recent"
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form || form.action == null) return;
    const action = form.getAttribute('action') || '';
    if (!action.includes('/search')) return;
    const input = form.querySelector('input[name="q"]');
    if (input) pushRecent(input.value);
  });

  // Also push recent when a predictive product/collection link is selected
  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('predictive-search a[href]');
    if (!link) return;
    const component = link.closest('predictive-search');
    const input = component && component.querySelector('input[type="search"]');
    if (input && input.value) pushRecent(input.value);
  });

  // Empty-state on focus when input is empty; also seed the cart state
  // so steppers can render as soon as results appear.
  document.addEventListener('focusin', (event) => {
    const input = event.target;
    if (!input || input.tagName !== 'INPUT' || input.type !== 'search') return;
    const component = input.closest('predictive-search');
    if (!component) return;
    ensureCartState();
    if (input.value && input.value.trim().length) return;
    showEmptyState(component);
  });

  /* ---------------------------------------------------------------- */
  /* Keep steppers + the View-basket bar in sync.                      */
  /* The dropdown is re-rendered with fresh server HTML on every      */
  /* keystroke (Add buttons only), so re-apply basket state after     */
  /* each render. syncAllRows is idempotent, which lets the observer  */
  /* settle instead of looping on its own DOM writes.                 */
  /* ---------------------------------------------------------------- */
  document.addEventListener('DOMContentLoaded', () => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const cart = event && event.cartData;
        if (cart && Array.isArray(cart.items)) applyCartState(cart);
      });
    }

    let renderTimer = null;
    const renderObserver = new MutationObserver(() => {
      clearTimeout(renderTimer);
      renderTimer = setTimeout(() => {
        if (cartQty) syncAllRows();
        else ensureCartState();
      }, 60);
    });
    document.querySelectorAll('predictive-search').forEach((component) => {
      renderObserver.observe(component, { childList: true, subtree: true });
    });
  });

  /* ---------------------------------------------------------------- */
  /* Click-outside-to-close.                                           */
  /* Shopify's predictive-search only closes on focusout, which       */
  /* doesn't fire when you click on plain body content. This handler  */
  /* closes every open <predictive-search> when the user mousedowns   */
  /* outside it.                                                      */
  /* ---------------------------------------------------------------- */
  document.addEventListener('mousedown', (event) => {
    const opened = document.querySelectorAll('predictive-search[open], predictive-search[results]');
    if (!opened.length) return;
    opened.forEach((component) => {
      if (component.contains(event.target)) return;
      // Also tolerate a moved-to-body dropdown (legacy code path) if present
      const wrapper = component.querySelector('[data-predictive-search]');
      if (wrapper && wrapper.contains(event.target)) return;
      if (typeof component.close === 'function') {
        component.close();
      } else {
        component.removeAttribute('open');
        component.removeAttribute('results');
        component.removeAttribute('loading');
      }
    });
  });

  // Esc closes any open predictive-search component
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('predictive-search[open], predictive-search[results]').forEach((component) => {
      if (typeof component.close === 'function') component.close(true);
      else {
        component.removeAttribute('open');
        component.removeAttribute('results');
      }
    });
  });

  /* ---------------------------------------------------------------- */
  /* Rotating placeholder                                              */
  /* Cycles the search input placeholder through the popular search   */
  /* terms so users see ideas of what they can search for.            */
  /* Pauses while the input is focused or has a value.                */
  /* ---------------------------------------------------------------- */
  function startPlaceholderRotation() {
    const terms = (window.DBMSearchPopular || []).map((t) => String(t).trim()).filter(Boolean);
    if (terms.length < 2) return;

    const inputs = document.querySelectorAll('.dbm-search input[type="search"]');
    if (!inputs.length) return;

    inputs.forEach((input) => {
      if (input.dataset.dbmRotateBound === '1') return;
      input.dataset.dbmRotateBound = '1';

      const basePrefix = 'Search building materials, brands, SKUs… ';
      let index = 0;
      let intervalId = null;
      let paused = false;

      const apply = () => {
        if (paused) return;
        if (input.value && input.value.length) return;
        input.classList.add('is-rotating');
        setTimeout(() => {
          if (input.value && input.value.length) {
            input.classList.remove('is-rotating');
            return;
          }
          input.placeholder = `${basePrefix}try “${terms[index]}”`;
          input.classList.remove('is-rotating');
          index = (index + 1) % terms.length;
        }, 280);
      };

      const start = () => {
        if (intervalId) return;
        apply();
        intervalId = setInterval(apply, 3500);
      };
      const stop = () => {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      };

      input.addEventListener('focus', () => {
        paused = true;
        stop();
        input.placeholder = basePrefix.trim();
      });
      input.addEventListener('blur', () => {
        if (!input.value || !input.value.length) {
          paused = false;
          start();
        }
      });

      // Pause if the user has reduced-motion preference
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        input.placeholder = basePrefix + 'e.g. cement, sand, tiles';
        return;
      }

      // Stagger initial start a touch so we don't see jank with FCP
      setTimeout(start, 1200);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startPlaceholderRotation);
  } else {
    startPlaceholderRotation();
  }
})();
